/**
 * The headline strip above the hourly board. See `design-notes.md`.
 *
 * Report-local cards rendering server-computed values (§4.6). There is no arithmetic in this file:
 * pace, gap, the projection and the last hour are each a governed measure from the `totals` view,
 * because every one of them is a ratio or a forecast over the whole selection and §4.3 keeps those
 * in SQL. If a figure here looks wrong, the fix is a column on `totals.sql`.
 */

import { cn } from '@/lib/utils';

import { count, signedCount, wholePercent } from './columns';
import { cellStatus, cellTone } from './status';
import type { Tier } from './columns';

/** One row of the `totals` view. Values arrive already in their declared type (§5.7). */
export type BoardTotals = Record<string, unknown>;

function num(source: BoardTotals | undefined, field: string): number | null {
  if (source === undefined) return null;
  const value = source[field];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function str(source: BoardTotals | undefined, field: string): string {
  if (source === undefined) return '';
  const value = source[field];
  return typeof value === 'string' ? value : '';
}

interface Scale {
  strip: string;
  label: string;
  figure: string;
  against: string;
  chip: string;
}

/** The same tiers the rows use, so a dense floor's strip gives back the height its rows need. */
const SCALE: Record<Tier, Scale> = {
  xl: {
    strip: 'gap-x-10 px-3 py-4',
    label: 'text-base tracking-widest',
    figure: 'text-6xl',
    against: 'text-xl',
    chip: 'text-lg px-3 py-1 rounded-lg',
  },
  lg: {
    strip: 'gap-x-8 px-3 py-3',
    label: 'text-sm tracking-widest',
    figure: 'text-5xl',
    against: 'text-lg',
    chip: 'text-base px-2.5 py-1 rounded-lg',
  },
  md: {
    strip: 'gap-x-8 px-2 py-2',
    label: 'text-xs tracking-widest',
    figure: 'text-4xl',
    against: 'text-base',
    chip: 'text-sm px-2 py-0.5 rounded-md',
  },
  sm: {
    strip: 'gap-x-6 px-2 py-1.5',
    label: 'text-xs tracking-widest',
    figure: 'text-3xl',
    against: 'text-sm',
    chip: 'text-xs px-2 py-0.5 rounded-md',
  },
  desk: {
    strip: 'gap-x-8 px-1 py-3',
    label: 'text-xs tracking-widest',
    figure: 'text-3xl',
    against: 'text-sm',
    chip: 'text-xs px-2 py-0.5 rounded-md',
  },
};

/** A headline figure and what it is measured against — §4.6: a figure with a target shows it. */
function Figure({
  label,
  value,
  against,
  tone,
  children,
  s,
}: {
  label: string;
  value: string;
  against: string;
  tone: string;
  children?: React.ReactNode;
  s: Scale;
}) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <span className={cn('font-semibold text-muted-foreground uppercase', s.label)}>{label}</span>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className={cn('leading-none font-bold tabular-nums', s.figure, tone)}>{value}</span>
        <span className={cn('text-muted-foreground', s.against)}>{against}</span>
        {children}
      </div>
    </div>
  );
}

/** A hairline between fields, as the design has. Decorative, so it is hidden from the reader. */
function Divider() {
  return <div className="w-px shrink-0 self-stretch bg-border" aria-hidden />;
}

export function BoardHeadline({
  totals,
  isLoading,
  tier,
}: {
  totals: BoardTotals | undefined;
  isLoading: boolean;
  tier: Tier;
}) {
  const s = SCALE[tier];

  if (isLoading || totals === undefined) {
    return (
      <div className={cn('animate-pulse rounded-lg bg-muted', tier === 'desk' ? 'h-20' : 'h-24')} />
    );
  }

  const elapsed = num(totals, 'elapsed_timeslot_count');
  const dayHours = num(totals, 'timeslot_count');
  const paceTone = cellTone(cellStatus(totals['pace_status'])).text;
  const gap = num(totals, 'gap_to_plan_now');
  // The hour it started, as the column headings show it — the full range would wrap the label.
  const lastHour = str(totals, 'last_timeslot').split('-')[0]?.trim() ?? '';
  const onPlan = num(totals, 'last_hour_lines_on_plan');
  const offPlan = num(totals, 'last_hour_lines_off_plan');

  // Before the first hour closes there is no pace, no gap and no last hour to report. Saying so
  // beats printing 0% against a plan the day has not had a chance to miss yet.
  if (elapsed === null || elapsed === 0) {
    return (
      <div className={cn('flex items-center border-b border-border', s.strip)}>
        <span className={cn('text-muted-foreground', s.against)}>
          The first hour of the day has not finished yet. Figures appear as each hour closes.
        </span>
      </div>
    );
  }

  return (
    // One row always, same rule as the sibling boards: columns share width rather than wrapping
    // the strip onto a second line and stealing height from the rows.
    <div className={cn('flex flex-nowrap items-stretch border-b border-border', s.strip)}>
      <Figure
        s={s}
        label="Perf to plan · to now"
        value={wholePercent(num(totals, 'pace_pct'))}
        against={`${count(num(totals, 'total_output'))} / ${count(num(totals, 'planned_quantity_to_now'))} pcs · ${count(elapsed)} of ${count(dayHours)} hours`}
        tone={paceTone}
      />
      <Divider />
      <Figure
        s={s}
        label="Gap to plan"
        value={signedCount(gap)}
        against="pcs against the plan for the hours done"
        tone={gap !== null && gap >= 0 ? cellTone('ON_TARGET').text : cellTone('BELOW_TARGET').text}
      />
      <Divider />
      <Figure
        s={s}
        label="Projected day end"
        value={wholePercent(num(totals, 'projected_day_pct'))}
        against={`${count(num(totals, 'projected_day_output'))} of ${count(num(totals, 'planned_quantity_per_day'))} at today's rate`}
        tone={cellTone(cellStatus(totals['projected_day_status'])).text}
      />
      <Divider />
      <Figure
        s={s}
        label={lastHour === '' ? 'Last full hour' : `Last full hour · ${lastHour}`}
        value={wholePercent(num(totals, 'last_hour_pct'))}
        against={`${count(num(totals, 'last_hour_output'))} / ${count(num(totals, 'planned_quantity_per_hour'))} pcs`}
        tone={cellTone(cellStatus(totals['last_hour_status'])).text}
      >
        {/* Counts of lines the SERVER classified — React never decides which side a line falls on. */}
        <span className="flex flex-wrap gap-1.5">
          <span
            className={cn('font-semibold whitespace-nowrap', s.chip, cellTone('ON_TARGET').wash)}
          >
            {`${count(onPlan)} hit plan`}
          </span>
          <span
            className={cn('font-semibold whitespace-nowrap', s.chip, cellTone('BELOW_TARGET').wash)}
          >
            {`${count(offPlan)} missed`}
          </span>
        </span>
      </Figure>
    </div>
  );
}
